import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

interface AuthGuardProps {
  children: React.ReactNode;
}

interface DecodedToken {
  exp: number;
  [key: string]: any;
}

// Global handler for API failures
const setupApiErrorHandling = (logout: () => void) => {
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    if (response.status === 401) {
      logout();
      throw new Error("Authentication failed");
    }

    return response;
  };
};

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  const logout = () => {
    localStorage.clear();
    const redirectPath = encodeURIComponent(location.pathname + location.search);
    window.location.href = `https://dev.api.connect.dalmlabs.com?redirect=${redirectPath}`;
  };

  // Synchronous initial check before render
  const checkAuthSync = () => {
    const token = localStorage.getItem("authToken");

    if (!token) {

      logout();
      return false;
    }

    try {
      const decoded: DecodedToken = jwtDecode(token);
      const isExpired = decoded.exp * 1000 < Date.now();

      if (isExpired) {
        logout();
        return false;
      }

      return true;
    } catch (error) {
      logout();
      return false;
    }
  };

  useEffect(() => {
    // Setup global API error handling
    setupApiErrorHandling(logout);

    // Initial authentication check
    if (checkAuthSync()) {
      setIsAuthenticated(true);

      // Async check for token expiry
      const token = localStorage.getItem("authToken");
      if (token) {
        try {
          const decoded: DecodedToken = jwtDecode(token);
          const timeUntilExpiry = decoded.exp * 1000 - Date.now();
          if (timeUntilExpiry > 0) {
            const timeoutId = setTimeout(() => {

              logout();
            }, timeUntilExpiry);
            return () => clearTimeout(timeoutId);
          }
        } catch (error) {
          logout();
        }
      }

      // Redirect from home to dashboard if authenticated
      if (window.location.pathname === "/") {
        navigate("/dashboard", { replace: true });
      }
    } else {
      setIsAuthenticated(false);
    }
  }, [navigate, location]);

  // Show loading state while checking auth
  if (isAuthenticated === null) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        Checking authentication... (as of {new Date().toLocaleString("en-US", { timeZone: "Africa/Lagos" })})
      </div>
    );
  }

  // If not authenticated, don't render children (redirect already triggered)
  if (!isAuthenticated) {
    return null;
  }

  // Authenticated - render children
  return <>{children}</>;
};

export default AuthGuard;